import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core'
import {distinctUntilChanged, interval, Observable, of, Subject, takeUntil} from 'rxjs'
import {ActivatedRoute, Params, Router} from '@angular/router'
import {filter, map, switchMap, tap} from "rxjs/operators";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Backend, PaginatedResponse} from "@generated-backend-types";
import {isArray} from "ngx-bootstrap/chronos";
import {LocalStorageService} from "ngx-webstorage";
import {FormBuilder, FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {ToastrService} from "ngx-toastr";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {TableDisplayConfig} from '../../entities/table-display-config.entity';
import {TableFunctionList} from '../../entities/table-function-list.entity';
import {NotEnoughPermissionsError} from '../../errors/NotEnoughPermissionsError';
import {DecimalPipe, NgClass, NgIf} from '@angular/common';
import {TableDataComponent} from './table-data/table-data.component';
import {v4 as uuidv4} from 'uuid';
import {UserSessionService} from '../../services/user-session.service';
import {TablesService} from '../../services/tables/tables.service';
import {TableFilterFavoritesService} from '../../services/api/settings/table-filter-favorites.service';
import {FormErrorService} from '../../services/form-error.service';
import {TablePaginationComponent} from './table-pagination/table-pagination.component';
import {DropdownTableFilterComponent} from '../dropdown-table-filter/dropdown-table-filter.component';
import {TableFilterComponent} from '../table-filter/table-filter.component';
import {ObjectArrayContainsValuePipe} from '../../pipes/object-array-contains-value.pipe';
import {TableFavoriteSelectorComponent} from './table-favorite-selector/table-favorite-selector.component';
import {TableColumnSorterComponent} from './table-column-sorter/table-column-sorter.component';
import {areObjectsIdentical} from '../../utils/are-objects-identical';
import TableConfig = Backend.App.Resources.Dashboard.Meta.TableConfig;
import TableFilterFavoriteData = Backend.App.Data.Settings.TableFilterFavoriteData;
import TableFilterFavorite = Backend.App.Resources.Dashboard.Settings.TableFilterFavorite;
import TableFilterDisplayTypeEnum = Backend.App.Enums.TableFilterDisplayTypeEnum;

@Component({
  selector: 'app-table[id]',
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [ReactiveFormsModule, NgIf, NgClass, DecimalPipe, TranslatePipe, TableDataComponent, TablePaginationComponent, DropdownTableFilterComponent, TableFilterComponent, ObjectArrayContainsValuePipe, TableFavoriteSelectorComponent, TableColumnSorterComponent],
  standalone: true
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class TableComponent<T extends Record<string, any>> implements OnInit, AfterViewChecked, OnDestroy {

  public data?: PaginatedResponse<T>
  public isLoading = true;

  @Input() public id!: string;
  @Input() public additionalButton: TemplateRef<unknown> | null = null;
  @Input() public additionalButtonWidth: number = 120;
  @Input() public additionalButtonHeaderText: string = '';
  @ViewChild(TableDataComponent) public tableData?: TableDataComponent<T>;

  @Input() public routerLinkPrefix = ''
  @Input() public routerLinkSuffix = ''
  @Input() public routerLinkValue?: string;
  @Input() public routerErrorText?: string;
  @Input() public globalFilter?: Record<string, string> = {}
  @Input() public showFavorites = true
  @Input() public showEditLink = true
  @Input() public showTopRow = true
  @Input() public showBorder = true
  @Input() public reloadEmitter?: EventEmitter<void>

  @Input() public replaceParametersInUrl: Record<string, string> = {}
  @Input() public functionList?: TableFunctionList;
  @Input() public isSubtable: boolean = false;
  public openSorter = new EventEmitter<void>
  public showFilters = false

  public readonly randomUuid = uuidv4();

  @Input() public cellTemplate?: TemplateRef<unknown>

  @Input() public defaultSort = '-updated_at';

  @Input() public putTableParametersInUrl = true;


  public tableDisplayConfigChange: EventEmitter<TableDisplayConfig> = new EventEmitter<TableDisplayConfig>
  public tableDisplayConfig: TableDisplayConfig =
    {
      filter: {},
      page: 1,
      per_page: 30,
      sorts: '-updated_at',
      hidden_columns: '',
      column_order: '',
      column_widths: {},
      favorite: undefined,
      tableConfig: {} as TableConfig
    }
  public defaultTableDisplayConfig: TableDisplayConfig = this.tableDisplayConfig;

  public error?: string

  public TableFilterDisplayTypeEnum = TableFilterDisplayTypeEnum;

  public favoriteFormControl: FormControl<TableFilterFavorite[] | undefined> = new FormControl();
  public favoriteForm = this.fb.group({
    id: this.fb.control<string | undefined>(undefined),
    title: this.fb.control('', Validators.required), // filled in view
    filter: this.fb.control([]),
    column_order: this.nfb.control<string | null>(''),
    sorts: this.fb.control(''),
    per_page: this.fb.control(30),
    hidden_columns: this.fb.control(''),
    table_id: this.fb.control(''),
  })
  public reloadFavorites: EventEmitter<void> = new EventEmitter<void>();

  private appTableFilterHeightChange = new Subject<number | undefined>();
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    public readonly userSessionService: UserSessionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly tableService: TablesService,
    private readonly http: HttpClient,
    private readonly activatedRoute: ActivatedRoute,
    private readonly localStorageService: LocalStorageService,
    private readonly fb: NonNullableFormBuilder,
    private readonly nfb: FormBuilder,
    private readonly tableFilterFavoriteService: TableFilterFavoritesService,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly formErrorService: FormErrorService,
  ) {

    this.tableDisplayConfigChange.pipe(
      distinctUntilChanged((a, b) => this.areObjectsIdentical(a, b))
    ).subscribe(
      v => {
        this.updateUrl(v)
      }
    )
  }

  public ngOnInit() {
    this.initTableListener();

    this.tableDisplayConfig.sorts = this.defaultSort;
    this.defaultTableDisplayConfig.sorts = this.defaultSort;

    this.appTableFilterHeightChange.pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe({
        next: value => {
          const tableContainer = document.getElementById('table-container-' + this.randomUuid);
          if (value) {
            tableContainer?.setAttribute('style', 'height: calc(' + value + 'px + 55%);')
          } else {
            tableContainer?.setAttribute('style', 'height: 100%;')
          }
          this.cdr.markForCheck();
        }
      })

    this.favoriteFormControl.valueChanges.pipe(
      map(f => f?.[0] ?? null),
      distinctUntilChanged((a, b) => {
        return JSON.stringify(a) === JSON.stringify(b)
      })
    ).subscribe(
      f => {
        this.selectedFavorite(f)
      }
    );
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  public ngAfterViewChecked() {
    this.appTableFilterHeightChange.next(document.querySelector('app-table app-table-filter#expandable-table-filter')?.getBoundingClientRect()?.height);
  }

  public saveFavorite(): void {
    if (!this.tableDisplayConfig) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.favoriteForm.controls.filter.setValue(this.tableDisplayConfig.filter)
    this.favoriteForm.controls.column_order.setValue(this.tableDisplayConfig.column_order)
    this.favoriteForm.controls.sorts.setValue(this.tableDisplayConfig.sorts)
    this.favoriteForm.controls.per_page.setValue(this.tableDisplayConfig.per_page)
    this.favoriteForm.controls.hidden_columns.setValue(this.tableDisplayConfig.hidden_columns ?? '')
    this.favoriteForm.controls.table_id.setValue(this.tableDisplayConfig.tableConfig.id)


    this.favoriteForm.updateValueAndValidity();
    this.cdr.markForCheck()
    if (!this.favoriteForm.valid) {
      this.favoriteForm.markAllAsTouched();
      return;
    }

    of(this.favoriteForm.value).pipe(
      switchMap(data => {

        if (data.id) {
          return this.tableFilterFavoriteService.update(data.id, data as TableFilterFavoriteData);
        }

        return this.tableFilterFavoriteService.create(data as TableFilterFavoriteData);
      })
    ).subscribe({
      next: res => {
        this.toastrService.success(
          this.translate.instant('table_filter.saved_success')
        );
        this.reloadFavorites.emit();
        this.selectedFavorite(res.data)
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.favoriteForm.markAllAsTouched();
          this.favoriteForm.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.favoriteForm, err);
        } else {
          this.toastrService.error(
            this.translate.instant(
              this.translate.instant('table_filter.saved_error')
            )
          );
        }
        this.cdr.markForCheck();
      }
    });
  }

  public selectedFavorite(filterFavorite: TableFilterFavorite | null) {

    if (filterFavorite === null) {
      this.unloadFavorite()
      this.resetTable()
      return;
    }

    this.favoriteFormControl.setValue([filterFavorite]);

    this.favoriteForm.controls.id.setValue(filterFavorite.id);
    this.favoriteForm.controls.title.setValue(filterFavorite.title);

    const filters: { [param: string]: unknown } = {};

    Object.entries(filterFavorite.filter).forEach((i: string[]) => {
      if (i[1] !== null && i[1] !== undefined) filters[i[0]] = i[1];
    });

    const queryParams = {
      column_order: filterFavorite.column_order,
      favorite: filterFavorite.id,
      hidden_columns: filterFavorite.hidden_columns,
      per_page: filterFavorite.per_page,
      sorts: filterFavorite.sorts,
      filter: filters as unknown as { [param: string]: string; },
    }

    if (this.tableDisplayConfig) {
      this.tableDisplayConfigChange.emit({...this.tableDisplayConfig, ...queryParams})
    }
  }

  filtersChanged($event: TableDisplayConfig) {
    if (!this.areObjectsIdentical($event.filter, this.tableDisplayConfig.filter)) {
      this.tableDisplayConfigChange.next({...this.tableDisplayConfig, filter: $event.filter});
    }
  }

  columnsChanged($event: TableDisplayConfig) {
    this.tableDisplayConfigChange.next({
      filter: this.tableDisplayConfig.filter,
      page: this.tableDisplayConfig.page,
      per_page: this.tableDisplayConfig.per_page,
      sorts: this.tableDisplayConfig.sorts,
      column_widths: this.tableDisplayConfig.column_widths,
      favorite: this.tableDisplayConfig.favorite,
      tableConfig: this.tableDisplayConfig.tableConfig,
      column_order: $event.column_order,
      hidden_columns: $event.hidden_columns
    });
  }

  hiddenColumnsChanged($event: string) {
    this.tableDisplayConfigChange.next({
      filter: this.tableDisplayConfig.filter,
      page: this.tableDisplayConfig.page,
      per_page: this.tableDisplayConfig.per_page,
      sorts: this.tableDisplayConfig.sorts,
      column_widths: this.tableDisplayConfig.column_widths,
      favorite: this.tableDisplayConfig.favorite,
      tableConfig: this.tableDisplayConfig.tableConfig,
      column_order: this.tableDisplayConfig.column_order,
      hidden_columns: $event
    });
  }

  public goToPage(page: number): void {
    this.tableDisplayConfigChange.next({...this.tableDisplayConfig, page});
  }

  public sort(sorts: string) {
    this.tableDisplayConfigChange.next({...this.tableDisplayConfig, sorts, page: 1});
  }

  public columnWidthsChanged(column_widths: { [param: string]: number }) {
    this.tableDisplayConfigChange.next({...this.tableDisplayConfig, column_widths});
  }

  public perPage(per_page: number) {
    this.tableDisplayConfigChange.next({...this.tableDisplayConfig, per_page});
  }

  filterLoaded($event: TableDisplayConfig) {

    if (JSON.stringify($event) !== JSON.stringify(this.tableDisplayConfig)) {
      this.tableDisplayConfigChange.next($event);
    }

  }

  public resetTable() {
    this.localStorageService.clear(this.getStorageKey());
    this.initTableListener();
    this.tableDisplayConfigChange.next(this.defaultTableDisplayConfig);
  }

  showFilter($event: boolean) {
    this.showFilters = $event
    // in timeout to prevent ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      if (this.tableData) {
        this.tableData.render();
      }
      this.cdr.detectChanges()
    }, 20)


  }

  public toggleFilter() {
    this.showFilter(!this.showFilters)
    this.cdr.detectChanges();
  }

  private loadFavorite(id: string) {
    this.tableFilterFavoriteService.get(id).subscribe(
      (d) => {
        this.selectedFavorite(d.data)
      }
    )
  }

  private unloadFavorite() {
    this.favoriteFormControl.reset(undefined);
    this.favoriteForm.controls.id.reset(undefined);
    this.favoriteForm.controls.title.setValue('');
  }

  private registerResizeObserver() {
    const int = interval(200).subscribe(() => {
      const emitter = this.tableData?.tableDataComponent?.resize;
      if (emitter) {
        int.unsubscribe()
        emitter.pipe(
          map(() => {
            return this.tableData?.tableDataComponent?.headerComponent._columns.filter(c => c.prop).map(c => {
              console.debug(c.prop, c.width);
              return {[c.prop as string]: c.width as number}
            }) ?? []
          }),
          map((column_widths: { [prop: string]: number } []) => {
            console.debug(column_widths);
            return Object.assign({}, ...column_widths)
          })
        ).subscribe((value) => {
          console.debug(value);
          this.columnWidthsChanged(value)
        });

      }
    })
  }

  private initTableListener(): void {
    let config: TableConfig;

    this.reloadEmitter?.pipe(
      map(() => this.tableDisplayConfig),
      switchMap((config) => this.load(config))
    ).subscribe((data) => {
      this.data = data;
      this.isLoading = false;
      this.error = undefined;
      this.cdr.markForCheck()
      this.cdr.detectChanges()
    });

    this.tableService.getConfigurationForTable(this.id).pipe(
      map((tableConfig) => {
        if (tableConfig === undefined) {
          throw new Error('Unknown Table ID given');
        }
        config = tableConfig

        const filter: { [param: string]: string } = {}

        config.columns.map(c => {

          if (typeof c.default_filter === 'string') {
            filter[c.name] = c.default_filter;
          } else if (isArray(c.default_filter)) {
            filter[c.name] = c.default_filter.join(',')
          } else {
            // filter[c.name] = c.default_filter.join(',')
          }
        });


        this.defaultTableDisplayConfig = {
          filter,
          page: 1,
          per_page: 30,
          sorts: this.defaultSort,
          hidden_columns: config.columns.filter(c => c.initially_hidden).map(c => c.name).join(','),
          column_widths: {},
          column_order: config.columns.map(c => c.name).join(','),
          favorite: '',
          tableConfig: config
        }


      }),
      switchMap(() => this.activatedRoute.queryParams),

      map((r: Params): TableDisplayConfig => {


        let fallBackConfig: TableDisplayConfig;
        const storedConfig = this.localStorageService.retrieve(this.getStorageKey())
        if (storedConfig) {
          fallBackConfig = storedConfig
        } else {
          fallBackConfig = this.defaultTableDisplayConfig;
        }

        const columnWidthsArray: string[] = r['column_widths']?.split(',') ?? [];
        const column_widths: { [param: string]: number } = Object.assign({}, ...columnWidthsArray.map((c: string): { [key: string]: number } => {
          const [key, value] = c.split(':');
          return {[key]: parseInt(value, 10)};
        }), fallBackConfig.column_widths);

        const filter = {...fallBackConfig.filter, ...this.urlRepresentationToFilter(r)}

        if (this.isSubtable) {
          return {
            filter,
            page: fallBackConfig.page,
            per_page: fallBackConfig.per_page,
            sorts: fallBackConfig.sorts,
            hidden_columns: fallBackConfig.hidden_columns,

            column_widths,
            column_order: fallBackConfig.column_order,
            favorite: fallBackConfig.favorite,
            tableConfig: config
          }
        } else {
          return {
            filter,
            page: parseInt(r['page'] ?? fallBackConfig.page),
            per_page: parseInt(r['per_page'] ?? fallBackConfig.per_page),
            sorts: r['sorts'] ?? fallBackConfig.sorts,
            hidden_columns: r['hidden_columns'] ?? fallBackConfig.hidden_columns,

            column_widths,
            column_order: r['column_order'] ?? fallBackConfig.column_order,
            favorite: r['favorite'] ?? fallBackConfig.favorite,
            tableConfig: config
          };
        }
      }),
      filter((newConfig: TableDisplayConfig) => {

        const shouldContinue = newConfig.tableConfig.id !== this.tableDisplayConfig.tableConfig.id
          || !this.areObjectsIdentical(newConfig.filter, this.tableDisplayConfig.filter)
          || newConfig.page !== this.tableDisplayConfig.page
          || newConfig.per_page !== this.tableDisplayConfig.per_page
          || newConfig.sorts !== this.tableDisplayConfig.sorts

        ///this.logObjectDifferences(newConfig.filter, this.tableDisplayConfig.filter)
        this.tableDisplayConfig = newConfig

        return shouldContinue
      }),
      tap(config => {
        if (config.tableConfig.filter_type === TableFilterDisplayTypeEnum.SINGLE_COLUMN_DROPDOWN_FILTER) {
          if (config.favorite) {
            if (!this.favoriteFormControl.value) {
              this.loadFavorite(config.favorite)
            }
          } else {
            if (this.favoriteFormControl.value) {
              this.unloadFavorite();
            }
          }
        }
      }),
      switchMap((config) => this.load(config)),
      // finalize(() => this.initTableListener())
    ).subscribe({
      next: (data) => {

        this.registerResizeObserver()
        this.data = data;
        this.isLoading = false;
        this.error = undefined;
        this.cdr.markForCheck()
      },
      error: (e) => {
        this.error = e.message;
        this.cdr.detectChanges();
      },
    })
  }

  private updateUrl(config: TableDisplayConfig, addToHistory = true) {
    if (!this.putTableParametersInUrl) return;

    const queryParams = {
      column_order: config.column_order ?? config.tableConfig.columns.map(c => c.name).join(','),
      favorite: config.favorite ?? '',
      hidden_columns: config.hidden_columns?.split(',').filter(e => e !== '').sort().join(',') ?? '', // fixed
      column_widths: Object.keys(config.column_widths).map(k => k + ':' + Math.round(config.column_widths[k])).join(','), // fixed
      page: config.page ?? 1, // fixed
      per_page: parseInt(config.per_page as unknown as string) ?? 30, // fixes
      sorts: config.sorts ?? '-updated_at', // fixed
      ...this.filterToUrlRepresentation(config)
    };
    // speichere die config im localstorage
    this.localStorageService.store(this.getStorageKey(), config)
    // console.debug('logging ' + this.tableDisplayConfig.tableConfig.id);
    this.router.navigate([], {
      queryParams,
      replaceUrl: addToHistory,
      onSameUrlNavigation: 'ignore'
    })
  }

  private filterToUrlRepresentation(config: TableDisplayConfig): Record<string, string> {
    const params: Record<string, string> = {};
    Object.keys(config.filter).forEach(key => {

      const name = 'filter[' + key + ']';
      const value = config.filter[key];
      if (value === '' || value === null) {
        params[name] = ''
      }
      params[name] = value

    });

    if (this.globalFilter) {
      Object.keys(this.globalFilter).forEach((key: string) => {
        if (this.globalFilter?.[key]) {
          params['filter[' + key + ']'] = this.globalFilter[key]
        }
      });
    }

    return params;
  }

  private urlRepresentationToFilter(params: Record<string, string>): { [param: string]: string } {
    const filter: { [param: string]: string } = {};
    Object.keys(params).forEach(key => {
      const match = key.match(/^filter\[(.*)\]$/);
      if (match) {
        const filterKey = match[1];
        filter[filterKey] = params[key];
      }
    });

    return filter
  }

  private load(config: TableDisplayConfig): Observable<PaginatedResponse<T>> {
    this.isLoading = true;
    if (!config.tableConfig?.url) {
      return new Observable()
    }
    let url = config.tableConfig.url.replace('api/dashboard/v1/', '/');

    if (this.replaceParametersInUrl) {
      Object.keys(this.replaceParametersInUrl).forEach((key) => {
        url = url.replace(key, this.replaceParametersInUrl[key])
      })
    }


    return this.http.get<PaginatedResponse<T>>(url, {
      params: {
        page: config.page,
        per_page: config.per_page,
        sort: config.sorts,
        ...this.filterToUrlRepresentation(config)

      }
    }).pipe(tap(p => {
      if (p.meta.per_page !== config.per_page) {
        this.perPage(p.meta.per_page)
      }
    }));
  }

  private areObjectsIdentical(obj1: { [key: string]: unknown }, obj2: { [key: string]: unknown }): boolean {
    return areObjectsIdentical(obj1, obj2);
  }

  private getStorageKey(): string {
    const key = 'table-config-' + this.id

    if (this.globalFilter && Object.keys(this.globalFilter).length > 0) {
      return key + '|global_filter_keys:' + (Object.keys(this.globalFilter).join(','));
    }
    return key
  }
}
